/**
 * What the overview, the category pages and search offer: the area tile and the
 * article row.
 *
 * The tile is the Material Compliance start page's own card, so an area looks like
 * the place in the product it explains. Articles are rows rather than cards: a
 * section of them scans like the setup checklist, and a row needs no neighbour to
 * look finished.
 */

import { StudioCard } from "@/features/material-compliance/components/home/StudioCard"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowRight } from "lucide-react"
import * as m from "motion/react-m"
import type { ReactNode } from "react"
import { Link, useNavigate } from "react-router"
import type { HelpArticle, HelpCategory } from "../api/types"
import { REVEAL_GROUP, REVEAL_ITEM } from "../motion"
import { helpArticlePath, helpCategoryPath } from "../routes"
import { CategoryIcon, visualIcon } from "./CategoryVisual"

export function CategoryTile({ category }: { category: HelpCategory }) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const count = translate("HELPDESK/ARTICLE_COUNT", {
    count: category.articleCount,
  })

  return (
    <StudioCard
      icon={visualIcon(category.visualKey)}
      title={category.title}
      question={category.description}
      stat={{
        value: count,
        caption: category.groups.map((group) => group.title).join(" · "),
        tone: "default",
      }}
      statCaption={count}
      roleLabel={category.tag}
      backTitle={category.title}
      backRows={category.groups.map((group) => ({
        label: group.title,
        value: String(group.articles.length),
      }))}
      ctaLabel={translate("HELPDESK/OPEN_AREA")}
      onOpen={() => void navigate(helpCategoryPath(category.id))}
      className="h-[340px] w-full lg:h-[340px]"
      // Three lines held open, so titles line up across tiles whose text is shorter.
      questionClassName="min-h-[3lh]"
    />
  )
}

/**
 * Sizes the rows against their own width, so a narrow column drops the meta. The
 * rows arrive one after the other.
 */
export function ArticleList({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <m.ul
      variants={REVEAL_GROUP}
      initial="hidden"
      animate="shown"
      className={cn("@container flex flex-col divide-y", className)}
    >
      {children}
    </m.ul>
  )
}

interface ArticleRowProps {
  article: HelpArticle
  categoryId: string
  /** The category's icon, where the row appears outside its own category. */
  visualKey?: string
  /** The category's name in the meta, for the same reason. */
  categoryTitle?: string
  /** Title only, for a short list of pointers. */
  compact?: boolean
}

export function ArticleRow({
  article,
  categoryId,
  visualKey,
  categoryTitle,
  compact = false,
}: ArticleRowProps) {
  const { translate } = useTranslation()
  const readingTime = translate("HELPDESK/READING_TIME", {
    minutes: article.minutes,
  })

  return (
    <m.li variants={REVEAL_ITEM}>
      <Link
        to={helpArticlePath(categoryId, article.id)}
        data-testid={`help-article-card-${article.id}`}
        className={cn(
          "group -mx-3 flex items-center gap-3 rounded-md px-3 transition-colors duration-150 hover:bg-accent/50",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          compact ? "py-2.5" : "py-3",
        )}
      >
        {visualKey ? <CategoryIcon visualKey={visualKey} /> : null}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-white">
            {article.title}
          </span>
          {compact ? null : (
            <span className="text-sm text-muted-foreground">
              {article.description}
            </span>
          )}
        </span>
        <span className="hidden shrink-0 text-xs text-muted-foreground @lg:block">
          {categoryTitle ? `${categoryTitle} · ${readingTime}` : readingTime}
        </span>
        <ArrowRight
          aria-hidden="true"
          className="size-4 shrink-0 text-muted-foreground transition-transform duration-150 group-hover:text-white motion-safe:group-hover:translate-x-0.5"
        />
      </Link>
    </m.li>
  )
}
